import CtaButtonGroup, { CtaType } from "@/components/molecules/CtaButtonGroup";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Text from "./shared/Text";

/**
 * PlatformMigrationV2Section — "Using another platform? We'll move
 * everything over for you."
 *
 * Two-column layout: LEFT is eyebrow + split title + description +
 * checklist + single CTA. RIGHT is an illustrated migration graphic.
 */

export type PlatformMigrationV2Data = {
  eyebrow: string;
  titleMuted: string;
  titleBright: string;
  titleMutedTail?: string;
  description: string;
  bullets: string[];
  cta: CtaType;
  image?: string;
  imageAlt?: string;
};

const PlatformMigrationV2Section = ({
  eyebrow,
  titleMuted,
  titleBright,
  titleMutedTail,
  description,
  bullets,
  cta,
  image,
  imageAlt,
}: PlatformMigrationV2Data) => {
  return (
    <section className="relative w-full overflow-hidden bg-white">
      <div className="container mx-auto px-4 py-10 sm:px-6 md:px-8 md:py-20 lg:px-12 lg:py-24">
        <div className="mx-auto max-w-6xl">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
          {/* LEFT */}
          <div>
            <div className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="inline-block h-2 w-2 rounded-full bg-[#2CA25F]"
              />
              <Text
                variant="eyebrow"
                className="text-[12px] font-semibold tracking-[0.14em] text-[#2CA25F] md:text-[13px]"
              >
                {eyebrow}
              </Text>
            </div>

            <Text
              as="h2"
              variant="display-lg"
              className="mt-4 text-[24px] leading-[1.15] sm:text-[24px] md:text-[44px] md:leading-[1.1] lg:text-[48px]"
            >
              <span className="text-neutralBlack">{titleMuted}</span>{" "}
              <span className="text-neutralBlack">{titleBright}</span>{" "}
              {titleMutedTail ? (
                <span className="text-neutralBlack">{titleMutedTail}</span>
              ) : null}
            </Text>

            <Text
              as="p"
              variant="body-lg"
              className="mt-5 max-w-xl text-[14px] leading-relaxed text-darkSlate04 md:text-[16px]"
            >
              {description}
            </Text>

            <ul className="mt-8 flex flex-col gap-4 md:mt-10">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <FontAwesomeIcon
                    icon={faCircleCheck}
                    aria-hidden="true"
                    className="mt-0.5 h-[18px] w-[18px] shrink-0 text-[#5766F2]"
                  />
                  <Text
                    as="span"
                    variant="body-md"
                    className="text-[14px] leading-snug text-neutralBlack md:text-[15px]"
                  >
                    {b}
                  </Text>
                </li>
              ))}
            </ul>

            <div className="mt-8 md:mt-10">
              <CtaButtonGroup
                ctas={[cta]}
                gap="flex"
                fullWidthOnMobile={false}
              />
            </div>
          </div>

          {/* RIGHT */}
          <div className="flex items-center justify-center lg:justify-end">
            {image ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={image}
                alt={imageAlt ?? ""}
                className="block w-full max-w-[560px] rounded-3xl"
              />
            ) : (
              <div className="aspect-square w-full max-w-[560px] rounded-3xl bg-gradient-to-br from-[#8CD3FF] via-[#A8DFFF] to-[#6CC2FF]" />
            )}
          </div>
        </div>
      </div>
      </div>
    </section>
  );
};

export default PlatformMigrationV2Section;
